import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lightning, Package, Users, CheckCircle } from '@phosphor-icons/react';
import API from '../lib/api';
import { useBranding } from '../lib/branding';

const ClientLogosMarquee = ({ logos }) => {
  if (!logos || logos.length === 0) return null;
  // Duplicate the list so the -50% translate wraps seamlessly.
  const doubled = [...logos, ...logos];
  // Slow down the animation the more logos we have so speed stays visually similar.
  const durationSec = Math.max(20, logos.length * 4);
  return (
    <div className="mt-5" data-testid="enterprise-clients">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-neutral-600">— Trusted by</div>
        <div className="text-[9px] tracking-[0.2em] uppercase text-neutral-400">Brands we print for</div>
      </div>
      <div
        className="pk-marquee-viewport relative overflow-hidden pk-border bg-white"
        style={{
          maskImage: 'linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)',
        }}
      >
        <div
          className="pk-marquee-track py-3"
          style={{ ['--pk-marquee-duration']: `${durationSec}s` }}
          role="list"
          aria-label="Brands we have served"
        >
          {doubled.map((c, i) => (
            <div
              key={`${c.name}-${i}`}
              role="listitem"
              className="flex items-center gap-2 mx-4 shrink-0"
              title={c.name || 'Client'}
              data-testid={i < logos.length ? `client-logo-${i}` : undefined}
            >
              <img
                src={c.logo_data_url}
                alt={c.name || 'client logo'}
                className="h-10 w-auto max-w-[120px] object-contain grayscale hover:grayscale-0 transition duration-300 opacity-80 hover:opacity-100"
                loading="lazy"
                draggable={false}
              />
              {c.name && (
                <span className="hidden sm:inline text-[10px] tracking-widest uppercase text-neutral-500 font-mono">
                  {c.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Enterprise = () => {
  const { branding } = useBranding();
  const [form, setForm] = useState({ company: '', name: '', phone: '', email: '', volume: '', products: '', message: '' });
  const [ok, setOk] = useState(false);
  const [clientLogos, setClientLogos] = useState(branding?.client_logos || []);

  // Keep local logos in sync with branding context updates
  useEffect(() => { setClientLogos(branding?.client_logos || []); }, [branding?.client_logos]);

  // Also directly fetch — some views may render before branding provider hydrates.
  useEffect(() => {
    let cancel = false;
    API.get('/branding')
      .then(r => { if (!cancel && Array.isArray(r.data?.client_logos)) setClientLogos(r.data.client_logos); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const upd = k => e => setForm({ ...form, [k]: k === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await API.post('/enterprise/inquiries', form); setOk(true); toast.success('Request received'); }
    catch (err) { toast.error('Failed'); }
  };
  return (
    <div className="p-6">
      <div className="reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Enterprise / Bulk</div>
        <h1 className="font-display text-[28px] mt-2 leading-tight">Big brand.<br/>Big volume.<br/><span style={{ color: 'var(--pk-primary)' }}>Best price.</span></h1>
      </div>
      <p className="mt-3 text-[13px] text-neutral-700">For salons, retail chains, election campaigns — our enterprise team calls within <b>15 minutes</b>.</p>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {[[Package,'Bulk pricing'],[Lightning,'15 min reply'],[Users,'Dedicated mgr']].map(([Ic, l]) => (
          <div key={l} className="pk-card p-3 text-center"><Ic size={20} weight="duotone" color="var(--pk-primary)" className="mx-auto"/><div className="text-[9px] tracking-[0.18em] font-bold uppercase mt-1">{l}</div></div>
        ))}
      </div>

      <ClientLogosMarquee logos={clientLogos} />

      {ok ? (
        <div className="mt-6 pk-card p-4 flex items-start gap-3">
          <CheckCircle size={22} weight="fill" color="var(--pk-primary)" />
          <div><div className="font-display text-[16px]">You're on our list.</div><div className="text-[12px] text-neutral-600 mt-1">Enterprise team will call within 15 minutes.</div></div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          {[['company','Company','text'],['name','Contact name','text'],['phone','Phone','tel'],['email','Email','email'],['volume','Estimated monthly volume','text'],['products','Products needed','text']].map(([k,l,ty]) => (
            <div key={k}><label className="pk-label">{l}</label><input required={['company','name','phone','email'].includes(k)} type={ty} className={`pk-input ${k==='phone'?'font-mono':''}`} value={form[k]} onChange={upd(k)} data-testid={`ent-${k}`} /></div>
          ))}
          <div><label className="pk-label">Message</label><textarea rows={3} className="pk-textarea" value={form.message} onChange={upd('message')} data-testid="ent-message" /></div>
          <button className="pk-btn w-full" data-testid="ent-submit">Request Enterprise Callback</button>
        </form>
      )}
    </div>
  );
};

export default Enterprise;
